import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Bomb, Check, HandCoins, RotateCw, TowerControl, Trophy } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

const ROW_CHOICES = [6, 8, 10];
const EDGE = 0.25;
const PRESETS = {
  easy: { cols: 4, bombs: 1, label: "Easy" },
  medium: { cols: 3, bombs: 1, label: "Medium" },
  hard: { cols: 2, bombs: 1, label: "Hard" },
  expert: { cols: 3, bombs: 2, label: "Expert" },
};

// Risk-based colours — cool & safe → hot & risky.
const DIFF_COLORS = {
  easy:   { grad: "linear-gradient(145deg,#34d399,#059669 58%,#065f46)", glow: "16,185,129", text: "#fff" },
  medium: { grad: "linear-gradient(145deg,#fcd34d,#f59e0b 58%,#b45309)", glow: "245,158,11", text: "#1a1205" },
  hard:   { grad: "linear-gradient(145deg,#fdba74,#f97316 55%,#c2410c)", glow: "249,115,22", text: "#fff" },
  expert: { grad: "linear-gradient(145deg,#fb7185,#ef4444 55%,#b91c1c)", glow: "244,63,94", text: "#fff" },
};
const ROW_COLORS = {
  6:  { grad: "linear-gradient(145deg,#67e8f9,#22d3ee 55%,#0e7490)", glow: "34,211,238", text: "#04222b" },
  8:  { grad: "linear-gradient(145deg,#fcd34d,#f59e0b 55%,#b45309)", glow: "245,158,11", text: "#1a1205" },
  10: { grad: "linear-gradient(145deg,#fb923c,#f97316 50%,#b91c1c)", glow: "249,115,22", text: "#fff" },
};

const rowMult = (level, cols, safe) => {
  const fair = Math.pow(cols / safe, level);
  return Math.min((1 - EDGE) * fair, 100);
};

function playStep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(660, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.22);
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch { /* audio blocked */ }
}

function playBoom() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const dur = 0.6;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 2);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 700;
    const g = ctx.createGain(); g.gain.setValueAtTime(0.5, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch { /* audio blocked */ }
}

export default function Tower() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [difficulty, setDifficulty] = useState("medium");
  const [rows, setRows] = useState(8);
  const [roundRows, setRoundRows] = useState(8);
  const [cfg, setCfg] = useState({ cols: 3, bombs: 1 });
  const [roundId, setRoundId] = useState(null);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [climbed, setClimbed] = useState(0);
  const [picks, setPicks] = useState([]);            // picked col per completed row
  const [multiplier, setMultiplier] = useState(1);
  const [bombLayout, setBombLayout] = useState(null); // revealed at end
  const [hit, setHit] = useState(null);               // { row, col }
  const [outcome, setOutcome] = useState(null);       // null | "won" | "lost"
  const [reveal, setReveal] = useState(null);
  const [recent, setRecent] = useState([]);
  const streakRef = useRef(0);
  const roundStakeRef = useRef(0);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="tower-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="tower-go-home">Go Home</Button></Link>
    </div>
  );

  const safe = cfg.cols - cfg.bombs;
  const R = (active || outcome) ? roundRows : rows;   // rows currently on the board

  const resetBoard = () => {
    setClimbed(0);
    setPicks([]);
    setMultiplier(1);
    setBombLayout(null);
    setHit(null);
    setOutcome(null);
  };

  const start = async () => {
    const s = Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio();
    setBusy(true);
    try {
      const res = await gamesApi.towerStart({ stake: s, difficulty, rows });
      resetBoard();
      roundStakeRef.current = s;
      setCfg({ cols: res.cols, bombs: res.bombs });
      setRoundRows(res.rows);
      setRoundId(res.roundId);
      setActive(true);
      setMultiplier(res.multiplier);
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const endWithWin = (mult, payout, layout) => {
    setBombLayout(layout || null);
    setActive(false);
    setOutcome("won");
    setMultiplier(mult);
    streakRef.current += 1;
    setRecent((prev) => [{ mult, won: true }, ...prev].slice(0, 12));
    playCoinSplash();
    setReveal({ label: `${Number(mult).toFixed(2)}x`, won: true, payout, streak: streakRef.current });
  };

  const pick = async (row, col) => {
    if (!active || busy || row !== climbed) return;
    setBusy(true);
    try {
      const res = await gamesApi.towerPick({ roundId, col });
      if (res.hitBomb) {
        setBombLayout(res.bombLayout || null);
        setHit({ row: res.row, col: res.col });
        setMultiplier(0);
        setActive(false);
        setOutcome("lost");
        streakRef.current = 0;
        playBoom();
        await refresh();
      } else {
        playStep();
        setPicks((prev) => [...prev, col]);
        setClimbed(res.climbed);
        setMultiplier(res.multiplier);
        if (res.cleared) {
          endWithWin(res.multiplier, res.payout, res.bombLayout);
          await refresh();
        }
      }
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const cashout = async () => {
    if (!active || busy || climbed < 1) return;
    setBusy(true);
    try {
      const res = await gamesApi.towerCashout({ roundId });
      endWithWin(res.multiplier, res.payout, res.bombLayout);
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const potential = Math.round(roundStakeRef.current * multiplier);

  // Cell visual state for a given row/col.
  const cellState = (row, col) => {
    const rowBombs = bombLayout ? bombLayout[row] : null;
    const isBomb = rowBombs ? rowBombs.includes(col) : false;
    if (hit && hit.row === row && hit.col === col) return "hit";
    if (row < climbed) {                       // a completed row
      if (picks[row] === col) return "safe";
      if (isBomb) return "bomb";
      return "dim";
    }
    if (outcome) {                             // game over, rows not reached
      if (isBomb) return "bomb";
      return "dim";
    }
    if (active && row === climbed) return "open";  // the current climbable row
    return "locked";
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 pb-28" data-testid="tower-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2">
            <TowerControl className="h-6 w-6 text-primary" /> Tower
          </h1>
          <p className="text-sm text-muted-foreground">Climb row by row · dodge the bombs · cash out anytime</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="tower-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Tower board */}
      <div className="relative rounded-2xl p-4 sm:p-6 overflow-hidden"
        style={{
          background: "radial-gradient(ellipse 130% 95% at 50% 0%, #1b2430 0%, #0e141c 55%, #05080c 100%)",
          border: "1px solid rgba(217,181,69,0.55)",
          boxShadow: "inset 0 0 0 3px rgba(217,181,69,0.22), inset 0 0 70px rgba(0,0,0,0.55), 0 10px 30px rgba(0,0,0,0.5)",
        }}>
        <div className="relative flex items-center justify-between mb-4">
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10" data-testid="tower-multiplier">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Multiplier</span>
            <span className="font-display font-black text-xl text-yellow-300">{Number(multiplier).toFixed(2)}x</span>
          </div>
          <div className="text-center text-xs text-emerald-200/80">
            {PRESETS[difficulty].label} · {cfg.bombs} bomb{cfg.bombs > 1 ? "s" : ""} / {cfg.cols} tiles
          </div>
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10 text-right">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Cash value</span>
            <span className="font-display font-black text-xl text-primary" data-testid="tower-potential">{active || outcome === "won" ? inr(potential) : "—"}</span>
          </div>
        </div>

        {/* Rows top (highest mult) to bottom (level 1) */}
        <div className="relative space-y-1.5" data-testid="tower-grid">
          {Array.from({ length: R }).map((_, idx) => {
            const row = R - 1 - idx;        // render top-down
            const level = row + 1;
            const isCurrent = active && row === climbed;
            const rm = rowMult(level, cfg.cols, safe);
            return (
              <div key={row} className={`flex items-center gap-2 rounded-xl p-1.5 transition-colors ${isCurrent ? "bg-yellow-400/10 ring-1 ring-yellow-400/50" : ""}`}
                data-testid={`tower-row-${row}`}>
                <div className={`w-16 shrink-0 text-right pr-1 font-display font-bold text-sm ${isCurrent ? "text-yellow-300" : "text-muted-foreground"}`}>
                  {rm.toFixed(2)}x
                </div>
                <div className="flex-1 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cfg.cols}, minmax(0, 1fr))` }}>
                  {Array.from({ length: cfg.cols }).map((__, col) => {
                    const st = cellState(row, col);
                    return (
                      <button
                        key={col}
                        data-testid={`tower-cell-${row}-${col}`}
                        onClick={() => pick(row, col)}
                        disabled={!isCurrent || busy}
                        className={`relative h-10 sm:h-11 rounded-lg grid place-items-center transition-[color,background-color,border-color,opacity,transform] duration-200 ${
                          st === "open" ? "hover:-translate-y-0.5 hover:brightness-125 active:scale-95 cursor-pointer" : "cursor-default"
                        }`}
                      >
                        <CellFace state={st} />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Status line */}
        <div className="relative mt-4 h-6 text-center text-sm">
          {outcome === "lost" && <span className="font-semibold text-red-400" data-testid="tower-status-lost">💥 Boom! You hit a bomb — bet lost.</span>}
          {outcome === "won" && <span className="font-semibold text-yellow-300" data-testid="tower-status-won">🏆 Cashed out {Number(multiplier).toFixed(2)}x · {inr(potential)}</span>}
          {active && <span className="text-emerald-100/70">Pick a tile on the glowing row to climb higher.</span>}
          {!active && !outcome && <span className="text-muted-foreground">Set your bet & difficulty, then Start Climb</span>}
        </div>

        {active && (
          <Button
            className="relative mt-3 w-full h-12 font-extrabold text-base"
            onClick={cashout}
            disabled={busy || climbed < 1}
            data-testid="tower-cashout-btn"
          >
            <HandCoins className="h-5 w-5 mr-1.5" />
            {climbed < 1 ? "Climb a row to enable cash out" : `Cash Out ${inr(potential)}`}
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="tower-controls">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Difficulty</div>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(PRESETS).map(([key, p]) => {
            const c = DIFF_COLORS[key];
            const on = difficulty === key;
            return (
              <button
                key={key}
                data-testid={`tower-difficulty-${key}`}
                onClick={() => { if (!active) { setDifficulty(key); setCfg({ cols: p.cols, bombs: p.bombs }); } }}
                disabled={active}
                style={on ? { background: c.grad, color: c.text, boxShadow: `0 0 18px rgba(${c.glow},0.5), inset 0 1px 0 rgba(255,255,255,0.35)` } : undefined}
                className={`rounded-xl py-3 text-center font-bold transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40 ${
                  on ? "scale-[1.03]" : "bg-secondary hover:bg-secondary/70 text-foreground"
                }`}
              >
                <Bomb className="h-4 w-4 mx-auto mb-0.5" style={on ? undefined : { color: `rgb(${c.glow})` }} />
                <span className="text-xs">{p.label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">Fewer safe tiles = bigger multipliers per row.</p>

        <div className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rows to the top</div>
        <div className="grid grid-cols-3 gap-2">
          {ROW_CHOICES.map((n) => {
            const c = ROW_COLORS[n];
            const on = rows === n;
            return (
              <button
                key={n}
                data-testid={`tower-rows-${n}`}
                onClick={() => { if (!active) setRows(n); }}
                disabled={active}
                style={on ? { background: c.grad, color: c.text, boxShadow: `0 0 18px rgba(${c.glow},0.5), inset 0 1px 0 rgba(255,255,255,0.35)` } : undefined}
                className={`rounded-xl py-3 text-center font-bold transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40 ${
                  on ? "scale-[1.03]" : "bg-secondary hover:bg-secondary/70 text-foreground"
                }`}
              >
                <span className="text-sm">{n} rows</span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">More rows = a higher climb and far bigger top multipliers.</p>

        <div className="mt-5">
          <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
          <Input type="number" min={20} max={100000} value={stake} disabled={active}
            onChange={(e) => setStake(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="tower-stake-input" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
              const special = q === 10000;
              return (
                <button key={q} onClick={() => !active && setStake(String(q))} disabled={active} data-testid={`tower-chip-${q}`}
                  className={special
                    ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40"
                    : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70 disabled:opacity-40"}
                  style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                  +{q}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          className="mt-5 w-full h-12 font-extrabold text-base"
          onClick={start}
          disabled={busy || active}
          data-testid="tower-start-btn"
        >
          <TowerControl className="h-5 w-5 mr-1.5" />
          {active ? "Round in progress…" : busy ? "Starting…" : "Start Climb"}
        </Button>

        {recent.length > 0 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="tower-recent">
            <RotateCw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {recent.map((r, i) => (
              <span key={i} className="shrink-0 rounded-full bg-primary/15 text-yellow-300 px-2.5 py-1 text-xs font-bold">
                {Number(r.mult).toFixed(2)}x
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CellFace({ state }) {
  if (state === "safe") {
    return (
      <span className="grid place-items-center h-full w-full rounded-lg"
        style={{
          background: "radial-gradient(circle at 50% 32%, #0e5c3a 0%, #073a25 100%)",
          boxShadow: "inset 0 0 10px rgba(0,0,0,0.4), 0 0 10px rgba(16,185,129,0.35)",
          border: "1px solid rgba(52,211,153,0.5)",
        }}>
        <Check className="h-5 w-5 text-emerald-300 drop-shadow-[0_0_6px_rgba(52,211,153,0.9)]" />
      </span>
    );
  }
  if (state === "bomb" || state === "hit") {
    const isHit = state === "hit";
    return (
      <span className="grid place-items-center h-full w-full rounded-lg"
        style={{
          background: isHit
            ? "radial-gradient(circle at 50% 32%, #ef4444 0%, #7f1d1d 100%)"
            : "radial-gradient(circle at 50% 32%, #3a1414 0%, #1a0808 100%)",
          boxShadow: isHit ? "0 0 18px rgba(239,68,68,0.8)" : "inset 0 0 10px rgba(0,0,0,0.5)",
          border: isHit ? "1px solid rgba(248,113,113,0.9)" : "1px solid rgba(127,29,29,0.6)",
        }}>
        <Bomb className={`h-5 w-5 ${isHit ? "text-white" : "text-red-400/80"}`} />
      </span>
    );
  }
  if (state === "dim") {
    return <span className="h-full w-full rounded-lg bg-white/[0.03] ring-1 ring-white/5" />;
  }
  if (state === "open") {
    return (
      <span className="grid place-items-center h-full w-full rounded-lg"
        style={{
          background: "linear-gradient(145deg, #33404f 0%, #1c2430 60%, #101720 100%)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 0 10px rgba(217,181,69,0.25)",
          border: "1px solid rgba(217,181,69,0.5)",
        }}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: "rgba(217,181,69,0.8)" }} />
      </span>
    );
  }
  // locked
  return (
    <span className="grid place-items-center h-full w-full rounded-lg"
      style={{
        background: "linear-gradient(145deg, #232b36 0%, #151b24 60%, #0d131b 100%)",
        boxShadow: "inset 0 -2px 6px rgba(0,0,0,0.45)",
        border: "1px solid rgba(217,181,69,0.18)",
      }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: "rgba(217,181,69,0.3)" }} />
    </span>
  );
}
